import { useEffect, useState } from 'react';
import { getCookie } from 'cookies-next';

// Cookies are often set by other requests shortly after mount, so when watching
// we poll for the cookie (a plain read is not reactive) until it appears.
const POLL_INTERVAL_MS = 300;
const POLL_TIMEOUT_MS = 5000;

type UseCookieOptions = {
  /** Name of the cookie to track. */
  cookieName: string;
  /**
   * When true and the cookie is not yet present, poll until it appears or the
   * timeout elapses. Defaults to false (a one-time read on mount).
   */
  watch?: boolean;
};

type UseCookieResult = {
  /** Current cookie value, or undefined if not set. */
  value: ReturnType<typeof getCookie>;
  /** Whether the cookie is currently present. */
  hasCookie: boolean;
  /** Whether watching gave up before the cookie appeared. */
  timedOut: boolean;
};

/**
 * Reactively tracks a cookie by name, exposing its value and presence.
 *
 * A plain cookie read is not reactive, so when `watch` is true and the cookie is
 * absent this polls until it appears (e.g. Once another request sets it) or a
 * timeout elapses.
 */
export default function useCookie({
  cookieName,
  watch = false,
}: UseCookieOptions): UseCookieResult {
  const [value, setValue] = useState(() => getCookie(cookieName));
  const [timedOut, setTimedOut] = useState(false);

  const hasCookie = !!value;

  useEffect(() => {
    if (!watch || hasCookie) return;

    const start = Date.now();
    const intervalId = setInterval(() => {
      const current = getCookie(cookieName);
      if (current) {
        setValue(current);
        clearInterval(intervalId);
      } else if (Date.now() - start >= POLL_TIMEOUT_MS) {
        setTimedOut(true);
        clearInterval(intervalId);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [watch, hasCookie, cookieName]);

  return { value, hasCookie, timedOut };
}
